import { useRef } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { Alert, App, Form, Select } from 'antd'
import type { OrderGroup, OrderGroupManageInput } from '@super-mega/server/contracts/order'
import { ContractStatusModal } from '@/features/contract/index.ts'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { useOrderGroupMutation } from './hooks/use-order-group-mutation.ts'

export function OrderGroupUpdateModal({
  group,
  kind,
  onClose
}: {
  group: OrderGroup
  kind: 'customer' | 'salesperson' | 'contractStatus'
  onClose: () => void
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const mutation = useOrderGroupMutation()
  const submission = useRef<{ payload: string; key: string } | undefined>(undefined)
  const keyword = useRemoteKeyword()
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({ purpose: 'order.group.read', keyword: keyword.keyword }),
    enabled: kind === 'customer'
  })
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({ purpose: 'order.group.read', keyword: keyword.keyword }),
    enabled: kind === 'salesperson'
  })
  const options = kind === 'customer' ? customers : salespeople
  const save = async (change: OrderGroupManageInput['change']) => {
    const payload = JSON.stringify(change)
    if (submission.current?.payload !== payload) {
      submission.current = { payload, key: crypto.randomUUID() }
    }
    try {
      await mutation.mutateAsync({
        orderGroupId: group.id,
        expectedCustomerId: group.customerId,
        expectedCount: group.orderCount,
        idempotencyKey: submission.current.key,
        change
      })
      void message.success('订单组批量修改完成')
      onClose()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '订单组批量修改失败'))
      return false
    }
  }
  if (kind === 'contractStatus') {
    return (
      <ContractStatusModal
        label={`修改「${group.name}」中全部 ${group.rentalOrderCount} 个租赁订单、${group.serviceOrderCount} 个服务订单的合同状态，包含已作废合同。`}
        pending={mutation.isPending}
        onClose={onClose}
        onSubmit={async (status) => {
          await save({ kind, status })
        }}
      />
    )
  }
  return (
    <ModalForm<{ value?: number }>
      open
      title={kind === 'customer' ? '一键修改客户' : '一键修改业务员'}
      width="36rem"
      layout="vertical"
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      modalProps={{
        destroyOnHidden: true,
        closable: !mutation.isPending,
        mask: { closable: false },
        keyboard: !mutation.isPending
      }}
      submitter={{
        searchConfig: { submitText: '确认修改', resetText: '取消' },
        submitButtonProps: { loading: mutation.isPending, disabled: options.isError || options.isPending },
        resetButtonProps: { disabled: mutation.isPending }
      }}
      onFinish={(values) =>
        save(kind === 'customer' ? { kind, customerId: values.value! } : { kind, salespersonId: values.value ?? null })
      }
    >
      <Alert
        className="mb-4"
        showIcon
        type="info"
        title={`操作范围：${group.name} · 全部 ${group.orderCount} 个订单`}
        description={
          kind === 'customer'
            ? '同时修改订单组和组内全部订单的客户，同步草稿账单与租赁占用，保留业务员及服务对象资料。存在非草稿账单或收款关联时整组不修改。'
            : '同时修改组内全部租赁订单和服务订单的业务员，留空则清除。'
        }
      />
      {options.isError && (
        <Alert className="mb-4" type="error" showIcon title={getErrorMessage(options.error, '选项加载失败')} />
      )}
      <Form.Item
        name="value"
        label={kind === 'customer' ? '新客户' : '业务员'}
        rules={kind === 'customer' ? [{ required: true, message: '请选择客户' }] : []}
      >
        <Select
          allowClear
          disabled={mutation.isPending}
          loading={options.isFetching}
          options={options.data?.map(({ label, value }) => ({ label, value })) ?? []}
          placeholder={kind === 'customer' ? '请选择客户' : '请选择业务员，或留空清除'}
          showSearch={{ filterOption: false, onSearch: keyword.onSearch }}
        />
      </Form.Item>
    </ModalForm>
  )
}
